import { Dialog, Flex, Skeleton, Text } from "@radix-ui/themes";
import ReactPlayer from "react-player";
import { ProximityDetails, useScreenZoneProximityQuery } from "../../api/zone-api";
import { useEffect, useRef, useState } from "react";
import { Recipe as TRecipe, useRecipesQuery } from "../../api/firebase-api";
import { useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import "./recipe.css";

export const mockRecipe = {
  id: "123",
  bin_id: "7157",
  qr_url: "https://www.youtube.com/shorts/WC1Uf1rAX3A",
  sku_id: "297",
  sku_image: "https://tezzro.jetlink.com.tr/tezzro/variants/images/2925/512",
  sku_name: "Cityfarm Organik Pirinç 1kg",
  video_url: "https://www.youtube.com/shorts/WC1Uf1rAX3A",
};

const initialProximity = JSON.parse(localStorage.getItem("previous_proximity") || "null");

export function Recipe() {
  const { zoneId } = useParams();
  const { data: recipes } = useRecipesQuery();
  const [currentRecipe, setCurrentRecipe] = useState<TRecipe | null>(null);
  const [activeProximity, setActiveProximity] = useState<ProximityDetails | null>(null);
  const [isVideoEnded, setIsVideEnded] = useState(false);
  const [open, setOpen] = useState(false);
  const player = useRef<ReactPlayer | null>(null);
  const previousProximity = useRef<ProximityDetails | null>(initialProximity);

  const { currentData: latestProximity } = useScreenZoneProximityQuery(
    {
      zoneId: zoneId as string,
    },
    {
      pollingInterval: 200,
      skip: !zoneId,
    }
  );

  useScreenWakeLock();

  useEffect(() => {
    console.debug({
      latest_received_proximity: latestProximity,
      latest_accepted_proximity: activeProximity,
      screen_state: open ? "playing" : "default",
      is_video_ended: isVideoEnded,
      recipes,
    });

    if (latestProximity === undefined) return;

    if (latestProximity === null) {
      setIsVideEnded(true);
      setOpen(false);
      setCurrentRecipe(null);
      setIsVideEnded(false);
      return;
    }

    if (latestProximity.proximity_id != previousProximity.current?.proximity_id && (!open || isVideoEnded)) {
      const newRecipe = recipes?.find((recipe) => recipe.bin_id == String(latestProximity.closest_bin_id));

      if (!newRecipe) return;

      setOpen(true);
      setActiveProximity(latestProximity);
      setIsVideEnded(false);
      setCurrentRecipe(newRecipe);
    } else if (
      latestProximity.proximity_id !== activeProximity?.proximity_id &&
      latestProximity.track_id === activeProximity?.track_id
    ) {
      const skuBins = recipes
        ?.filter((item) => item.sku_id == currentRecipe?.sku_id)
        .map((item) => item.bin_id);

      if (skuBins?.includes(String(latestProximity.closest_bin_id))) {
        return;
      }

      const newRecipe = recipes?.find((recipe) => recipe.bin_id == String(latestProximity.closest_bin_id));

      if (!newRecipe) return;

      setOpen(true);
      setActiveProximity(latestProximity);
      setIsVideEnded(false);
      setCurrentRecipe(newRecipe);
    }

    previousProximity.current = latestProximity || null;
    localStorage.setItem("previous_proximity", JSON.stringify(latestProximity));
  }, [latestProximity, recipes, activeProximity, open, isVideoEnded]);

  return (
    <div>
      <Flex className="h-screen" align="center" justify="center">
        <video src="/loop.mp4" autoPlay muted loop className="w-full h-full"></video>
      </Flex>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Content className="!max-w-full h-[calc(100dvh)] p-0 !rounded-none">
          <Flex p="4" align="center">
            <Skeleton loading={!currentRecipe}>
              <figure className="min-w-20 border">
                <img className="w-20 h-20" src={currentRecipe?.sku_image || ""} />
              </figure>
            </Skeleton>
            <Flex direction="column" justify="center" ml="2">
              <Skeleton loading={!currentRecipe}>
                <Text weight="medium" className="text-base md:text-lg">
                  {currentRecipe?.sku_name || "Product name"}
                </Text>
              </Skeleton>
            </Flex>
            <div className="grow"></div>
            <div className="flex items-center min-w-20 w-20 h-20">
              <Skeleton loading={!currentRecipe}>
                <QRCode
                  value={currentRecipe?.qr_url || ""}
                  size={256}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  viewBox={`0 0 256 256`}
                ></QRCode>
              </Skeleton>
            </div>
          </Flex>
          <Skeleton loading={!currentRecipe}>
            <div className="w-full h-[calc(100%-114px)]">
              <ReactPlayer
                ref={player}
                key={currentRecipe?.video_url}
                width="100%"
                height="100%"
                muted
                config={{
                  youtube: {
                    playerVars: {
                      autoplay: 1,
                    },
                  },
                }}
                onEnded={() => {
                  setIsVideEnded(true);
                }}
                url={currentRecipe?.video_url}
              />
            </div>
          </Skeleton>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}

function useScreenWakeLock() {
  useEffect(() => {
    if ("WakeLock" in window && "request" in window.WakeLock) {
      let wakeLock: any = null;

      const requestWakeLock = () => {
        const controller = new AbortController();
        const signal = controller.signal;
        (window.WakeLock as any).request("screen", { signal }).catch((e: any) => {
          if (e.name === "AbortError") {
            console.log("Wake Lock was aborted");
          } else {
            console.error(`${e.name}, ${e.message}`);
          }
        });
        console.log("Wake Lock is active");
        return controller;
      };

      const handleVisibilityChange = () => {
        if (wakeLock !== null && document.visibilityState === "visible") {
          wakeLock = requestWakeLock();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("fullscreenchange", handleVisibilityChange);
    } else if ("wakeLock" in navigator && "request" in navigator.wakeLock) {
      let wakeLock: any = null;

      const requestWakeLock = async () => {
        try {
          wakeLock = await navigator.wakeLock.request("screen");
          wakeLock.addEventListener("release", () => {
            console.log("Wake Lock was released");
          });
          console.log("Wake Lock is active");
        } catch (e: any) {
          console.error(`${e.name}, ${e.message}`);
        }
      };

      const handleVisibilityChange = () => {
        if (wakeLock !== null && document.visibilityState === "visible") {
          requestWakeLock();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("fullscreenchange", handleVisibilityChange);
    } else {
      console.error("Wake Lock API not supported.");
    }
  }, []);
}
